'use client';

import React from 'react';
import { motion } from 'framer-motion';

import { Box, BoxProps } from '../box';
import { Flex } from '../flex';
import { Text } from '../text';
import { Badge, type BadgeColorPalette } from '../badge';
import { Icon } from '../icon';
import { Button } from '../button';
import { Skeleton } from '../skeleton';

// Motion div for animations (using native div to avoid Chakra prop conflicts)
const MotionDiv = motion.div;

export interface DetailHeroHeaderProps extends Omit<BoxProps, 'title'> {
  /** Main title */
  title: string;
  /** Subtitle or description */
  subtitle?: string;
  /** Status badge configuration */
  status?: {
    label: string;
    colorPalette?: BadgeColorPalette;
  };
  /** Additional badges */
  badges?: Array<{
    label: string;
    colorPalette?: BadgeColorPalette;
  }>;
  /** Icon to display in the hero */
  icon?: React.ReactNode;
  /** Icon background gradient colors [from, to] */
  iconGradient?: [string, string];
  /** Header gradient colors [from, to] */
  headerGradient?: [string, string];
  /** Action buttons */
  actions?: React.ReactNode;
  /** Secondary actions (e.g., more menu) */
  secondaryActions?: React.ReactNode;
  /** Back button configuration */
  backButton?: {
    label?: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  /** Loading state */
  isLoading?: boolean;
  /** Children for additional content */
  children?: React.ReactNode;
}

/**
 * DetailHeroHeader - Professional hero header for detail pages
 * Features gradient backgrounds, animated entry, and rich visual elements
 */
export const DetailHeroHeader: React.FC<DetailHeroHeaderProps> = ({
  title,
  subtitle,
  status,
  badges = [],
  icon,
  iconGradient = ['#3B82F6', '#1D4ED8'],
  headerGradient = ['#F8FAFC', '#EFF6FF'],
  actions,
  secondaryActions,
  backButton,
  isLoading = false,
  children,
  ...boxProps
}) => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  if (isLoading) {
    return (
      <Box
        position="relative"
        borderRadius="xl"
        overflow="hidden"
        padding={6}
        background={`linear-gradient(135deg, ${headerGradient[0]} 0%, ${headerGradient[1]} 100%)`}
        border="1px solid"
        borderColor="border.muted"
        {...boxProps}
      >
        <Flex gap={5} alignItems="center">
          <Skeleton width="72px" height="72px" borderRadius="xl" />
          <Box flex={1}>
            <Skeleton width="200px" height="32px" marginBottom={2} />
            <Skeleton width="300px" height="20px" marginBottom={3} />
            <Flex gap={2}>
              <Skeleton width="80px" height="24px" borderRadius="full" />
              <Skeleton width="60px" height="24px" borderRadius="full" />
            </Flex>
          </Box>
          <Flex gap={2}>
            <Skeleton width="100px" height="36px" borderRadius="md" />
            <Skeleton width="100px" height="36px" borderRadius="md" />
          </Flex>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      borderRadius="xl"
      overflow="hidden"
      background={`linear-gradient(135deg, ${headerGradient[0]} 0%, ${headerGradient[1]} 100%)`}
      border="1px solid"
      borderColor="border.muted"
      boxShadow="sm"
      {...boxProps}
    >
      <MotionDiv
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ position: 'relative' }}
      >
        {/* Decorative gradient orb */}
        <Box
          position="absolute"
          top="-50%"
          right="-10%"
          width="300px"
          height="300px"
          borderRadius="full"
          background={`radial-gradient(circle, ${iconGradient[0]}15 0%, transparent 70%)`}
          pointerEvents="none"
        />

        <Box position="relative" padding={6}>
          {/* Back button */}
          {backButton && (
            <MotionDiv variants={itemVariants} style={{ marginBottom: '16px' }}>
              <Button
                variant="ghost"
                size="sm"
                onClick={backButton.onClick}
                leftIcon={backButton.icon}
              >
                {backButton.label || 'Geri'}
              </Button>
            </MotionDiv>
          )}

          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            gap={5}
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            {/* Left section - Icon and info */}
            <Flex gap={5} alignItems="center" flex={1}>
              {/* Icon with gradient background */}
              {icon && (
                <MotionDiv
                  variants={iconVariants}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '72px',
                    height: '72px',
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${iconGradient[0]} 0%, ${iconGradient[1]} 100%)`,
                    boxShadow: `0 8px 24px ${iconGradient[0]}40`,
                    color: 'white',
                    flexShrink: 0,
                  }}
                >
                  <Icon size="xl">{icon}</Icon>
                </MotionDiv>
              )}

              {/* Title and badges section */}
              <MotionDiv variants={itemVariants} style={{ flex: 1, minWidth: 0 }}>
                {/* Title */}
                <Text
                  fontSize={{ base: '2xl', md: '3xl' }}
                  fontWeight="bold"
                  color="fg.default"
                  lineHeight="tight"
                  marginBottom={1}
                  truncate
                >
                  {title}
                </Text>

                {/* Subtitle */}
                {subtitle && (
                  <Text fontSize="md" color="fg.muted" marginBottom={3} lineClamp={2}>
                    {subtitle}
                  </Text>
                )}

                {/* Status and badges */}
                <Flex gap={2} flexWrap="wrap" alignItems="center">
                  {status && (
                    <Badge size="lg" colorPalette={status.colorPalette || 'blue'}>
                      {status.label}
                    </Badge>
                  )}
                  {badges.map((badge, index) => (
                    <Badge key={index} size="md" colorPalette={badge.colorPalette || 'gray'}>
                      {badge.label}
                    </Badge>
                  ))}
                </Flex>
              </MotionDiv>
            </Flex>

            {/* Right section - Actions */}
            {(actions || secondaryActions) && (
              <MotionDiv
                variants={itemVariants}
                style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {actions}
                {secondaryActions}
              </MotionDiv>
            )}
          </Flex>

          {/* Additional content (e.g., quick stats) */}
          {children && (
            <MotionDiv variants={itemVariants} style={{ marginTop: '24px' }}>
              {children}
            </MotionDiv>
          )}
        </Box>
      </MotionDiv>
    </Box>
  );
};

export default DetailHeroHeader;
